import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CalendarEntry } from "@joogoos/contracts";
import { api } from "../api.js";
import { usePref } from "../ui/prefs.js";
import { ACTIVITY_KEY } from "../ui/queryClient.js";
import { useNarrow } from "../ui/narrow.js";
import { NotBuilt, Tab, TabBar } from "./chrome.js";
import { PostQueue, QUEUE } from "./PostQueue.js";

type View = "month" | "week" | "list" | "queue";

/**
 * Calendar (03, 06). Three views, the chosen one remembered, and a switch that runs the grid from today rather
 * than from the first of the month — all three taken from the old client, which got this part right (08). List is
 * the view on a phone.
 *
 * A day holds two kinds of thing and they look different on purpose: something made and scheduled, and a plan,
 * which is a line of text with a date and nothing made yet. The old client's plan tried to describe the content in
 * advance; this one is a reminder, and making it opens the Studio with the day and the idea already in the chat.
 *
 * The scheduled half is real from 2026-09-20 ([25](../../../../docs/25-channels-and-publishing.md), ADR 0038): the
 * rows are `scheduled_posts`, one per channel, and their colour is what actually happened to them. The plan half is
 * real from 2026-09-26: the pieces of a prepared week not made yet (03 §prepare my week), and "Make it" opens one in
 * the Studio, where its Generate button carries its price.
 *
 * **To approve** is the fourth view (2026-09-26, [31](../../../../docs/31-posts-joogoos-drafts.md)): drafts waiting
 * for an owner, and approved posts the owner makes by hand. It is a view of this tab rather than a tab of its own
 * because it is the same rows one step earlier, and its count is the "waiting for you" the bar always promised.
 *
 * **A post that did not go out says why, and has a Retry** (2026-09-29). On live the header said "look on the
 * Calendar" and the Calendar drew a red dot and a refresh arrow that was not a button. The reason comes from the
 * route in words (`postFailure`); Retry is shown only where it could work, and a post sent again is watched until it
 * has gone or failed again. What failed in the week before the first day drawn is drawn above the grid, because the
 * header sends people here for a day after a failure and a grid that starts today would not have it on.
 */
export function Calendar({ onMake }: { onMake?: ((projectId: string) => void) | undefined } = {}) {
  const [chosen, setView] = usePref<View>("calendar.view", "month");
  // Seven columns in a phone's width is a grid of crushed cards, so a phone is shown the list whatever was chosen on a
  // desk — and the choice is kept for the desk rather than overwritten.
  const narrow = useNarrow();
  // The queue is a list already, so a phone keeps it; only the grids give way to the list.
  const view: View = narrow && chosen !== "queue" ? "list" : chosen;
  const [startToday, setStartToday] = usePref("calendar.startToday", true);
  // The posts sent again from here, watched until they have gone or failed again.
  const [sentAgain, setSentAgain] = useState<string[]>([]);
  const calendar = useQuery({
    queryKey: ["calendar"], queryFn: api.calendar,
    // Asked every five seconds while a post sent again is still waiting, so its card says what came of it without a
    // reload. Nothing else here polls: a calendar left open is a tab sitting still (17 §performance 4).
    refetchInterval: (q) => q.state.data?.entries.some((e) => e.status === "scheduled" && sentAgain.includes(e.id)) ? 5_000 : false,
  });
  const queue = useQuery({ queryKey: QUEUE, queryFn: () => api.postQueue() });
  // What a person has to act on: every draft, and every by-hand post whose time has come.
  const waiting = (queue.data?.drafts.length ?? 0) + (queue.data?.byHand.filter((p) => new Date(p.scheduledAt).getTime() <= Date.now()).length ?? 0);
  const entries = calendar.data?.entries ?? [];

  const days = useMemo(() => grid(view, startToday), [view, startToday]);
  const act: Act = { onMake, sentAgain, onSentAgain: (id) => setSentAgain((was) => [...was, id]) };
  const first = key(days[0]!), weekAgo = key(add(new Date(), -7));
  const missed = entries.filter((e) => e.status === "failed" && e.date < first && e.date >= weekAgo);
  const byDay = useMemo(() => {
    const m = new Map<string, CalendarEntry[]>();
    for (const e of entries) m.set(e.date, [...(m.get(e.date) ?? []), e].sort((a, b) => a.time.localeCompare(b.time)));
    return m;
  }, [entries]);

  return (
    <div className="h-full flex flex-col min-h-0">
      <TabBar
        title={month()}
        {...(calendar.data && !entries.length ? { note: nothingYet(calendar.data.connected) } : {})}
      >
        {(narrow ? (["list", "queue"] as const) : (["month", "week", "list", "queue"] as const)).map((v) => (
          <Tab key={v} on={view === v} onClick={() => setView(v)}>
            {v === "queue" ? `To approve${waiting ? ` · ${waiting}` : ""}` : v[0]!.toUpperCase() + v.slice(1)}
          </Tab>
        ))}
        {view === "queue" ? null : (
          <label className="ml-2 flex items-center gap-1.5 text-xs text-muted select-none cursor-pointer">
            <input type="checkbox" checked={startToday} onChange={(e) => setStartToday(e.target.checked)} className="accent-coral" />
            Start today
          </label>
        )}
        <span className="ml-auto text-[11.5px] text-muted">{waiting} waiting for you</span>
      </TabBar>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-5">
        {view === "queue" ? <PostQueue /> : (
          <>
            {calendar.data && !calendar.data.available ? <NotBuilt what="calendar" note={calendar.data.note} /> : null}
            {missed.length ? (
              <section aria-label="Did not go out" className="mb-4 max-w-[720px] flex flex-col gap-1.5">
                <h2 className="text-[11px] font-bold uppercase tracking-[.06em] text-bad">Did not go out</h2>
                {missed.map((e) => <Card key={e.id} entry={e} wide act={act} />)}
              </section>
            ) : null}
            {view === "list" ? <List days={days} byDay={byDay} act={act} /> : <Month days={days} byDay={byDay} week={view === "week"} act={act} />}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * What an empty calendar says, which depends on whether a post has anywhere to go. It asked for a channel to be
 * connected while X was (the first pilot round, 2026-10-05), which sends somebody to a page with nothing to do on it;
 * with one connected, the missing step is a post, and a post is scheduled from the Studio once it is made.
 */
function nothingYet(connected: string[]): string {
  if (!connected.length) return "nothing is scheduled yet — connect a channel on the Business tab and pick a time";
  const names = connected.length === 1 ? connected[0]! : `${connected.slice(0, -1).join(", ")} and ${connected.at(-1)!}`;
  return `nothing is scheduled yet — ${names} ${connected.length === 1 ? "is" : "are"} connected: make a post in the Studio and press Schedule`;
}

/** What a card can do: open a plan in the Studio, and send a failed post again. */
interface Act {
  onMake: ((projectId: string) => void) | undefined;
  /** The posts sent again from here: while one is still waiting its card says so. */
  sentAgain: string[];
  onSentAgain: (id: string) => void;
}

function Month({ days, byDay, week, act }: { days: Date[]; byDay: Map<string, CalendarEntry[]>; week: boolean; act: Act }) {
  const today = key(new Date());
  // The headings come from the first row rather than from a fixed Mon–Sun: with "start today" on, the grid runs
  // from today, and a column of Wednesdays under a heading that says Monday is worse than no heading at all.
  return (
    <div className="grid grid-cols-7 gap-px bg-line border border-line rounded-[10px] overflow-hidden">
      {days.slice(0, 7).map((d) => (
        <div key={key(d)} className="bg-page px-2 py-1.5 text-[10px] font-bold uppercase tracking-[.06em] text-muted">
          {d.toLocaleDateString(undefined, { weekday: "short" })}
        </div>
      ))}
      {days.map((d) => {
        const k = key(d);
        const items = byDay.get(k) ?? [];
        return (
          <div key={k} className={`bg-panel p-1.5 flex flex-col gap-1.5 ${week ? "min-h-56" : "min-h-28"} ${k === today ? "outline outline-coral -outline-offset-1" : ""}`}>
            <span className={`text-[11px] tabular-nums ${k === today ? "font-bold text-coral" : "text-muted"}`}>{d.getDate()}</span>
            {items.map((e) => <Card key={e.id} entry={e} act={act} />)}
          </div>
        );
      })}
    </div>
  );
}

function List({ days, byDay, act }: { days: Date[]; byDay: Map<string, CalendarEntry[]>; act: Act }) {
  const rows = days.map((d) => [d, byDay.get(key(d)) ?? []] as const).filter(([, items]) => items.length);
  if (!rows.length) return null;
  return (
    <div className="flex flex-col gap-2 max-w-[720px]">
      {rows.map(([d, items]) => (
        <div key={key(d)} className="flex gap-3">
          <div className="w-20 shrink-0 pt-1.5">
            <div className="text-[13px] font-semibold">{d.toLocaleDateString(undefined, { day: "numeric", month: "short" })}</div>
            <div className="text-[11px] text-muted">{d.toLocaleDateString(undefined, { weekday: "long" })}</div>
          </div>
          <div className="flex-1 flex flex-col gap-1.5">{items.map((e) => <Card key={e.id} entry={e} wide act={act} />)}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * A scheduled post is a thing; a plan is a dashed outline with a button, because it is not a thing yet. A post that
 * did not go out is a thing with a red edge, the reason under it, and Retry where trying again could work.
 */
function Card({ entry, wide, act }: { entry: CalendarEntry; wide?: boolean; act: Act }) {
  if (entry.kind === "plan") {
    return (
      <div className={`rounded-lg border border-dashed border-line-2 px-2 py-1.5 flex items-center gap-2 ${wide ? "" : "flex-col items-start gap-1"}`}>
        <span className="min-w-0 flex-1">
          <span className="block text-[11.5px] font-medium truncate">{entry.title}</span>
          <span className="block text-[10.5px] text-muted">{entry.time} · {entry.makes ?? "post"}</span>
        </span>
        <button
          onClick={() => entry.projectId && act.onMake?.(entry.projectId)}
          className="shrink-0 px-2 py-1 rounded-md bg-coral text-white text-[10.5px] font-semibold"
        >
          Make it
        </button>
      </div>
    );
  }
  const head = (
    <div className="flex items-center gap-2">
      {entry.thumbUrl ? <img src={entry.thumbUrl} alt="" loading="lazy" decoding="async" className="w-6 h-8 rounded object-cover shrink-0" /> : null}
      <span className="min-w-0 flex-1">
        <span className="block text-[11.5px] font-medium truncate">{entry.title}</span>
        <span className="flex items-center gap-1 text-[10.5px] text-muted">
          <span className={`w-1.5 h-1.5 rounded-full ${entry.status === "published" ? "bg-good" : entry.status === "failed" ? "bg-bad" : "bg-coral"}`} />
          {entry.time} · {entry.channels.join(", ") || "no channel"}
        </span>
      </span>
    </div>
  );
  if (entry.status === "failed") {
    return (
      <article aria-label={`Did not go out on ${entry.channels.join(", ")}`} className="rounded-lg bg-page border border-bad px-2 py-1.5 flex flex-col gap-1.5">
        {head}
        <p className="text-[11px] leading-snug text-ink-2">{entry.failure?.why ?? "It did not go out."}</p>
        <div className="flex flex-wrap items-start gap-2">
          {entry.failure?.retry ? <Retry id={entry.id} onSent={act.onSentAgain} /> : null}
          <TakeBack id={entry.id} label="Remove" yes="Yes, remove it" />
        </div>
      </article>
    );
  }
  // A post that went out opens where it went, in a new tab: the post itself, or the account's page (`entry.url`) —
  // from a button that names the network (2026-09-29): the whole card as a link, with a small arrow, went unseen.
  if (entry.status === "published") {
    const where = entry.channels.join(", ");
    return (
      <article aria-label={`Went out on ${where}`} className="rounded-lg bg-page border border-line px-2 py-1.5 flex flex-col gap-1.5">
        {head}
        {entry.url ? (
          <a href={entry.url} target="_blank" rel="noopener noreferrer"
            className="self-start px-2 py-1 rounded-md border border-line-2 bg-panel text-[10.5px] font-semibold hover:bg-page">
            Open on {where} <span aria-hidden>↗</span>
          </a>
        ) : null}
        <TakeBack id={entry.id} label="Remove from calendar" yes="Yes, remove it"
          note={`This only takes it off your calendar. It stays on ${where} until you delete it there.`} />
      </article>
    );
  }
  return (
    <article aria-label={`Going out on ${entry.channels.join(", ")}`} className="rounded-lg bg-page border border-line px-2 py-1.5 flex flex-col gap-1">
      {head}
      {entry.status === "scheduled" && act.sentAgain.includes(entry.id)
        ? <p className="text-[10.5px] text-muted">Sent again — it goes out in about a minute.</p>
        : null}
      {entry.status === "scheduled" ? <TakeBack id={entry.id} label="Take back" yes="Yes, take it back" /> : null}
    </article>
  );
}

/**
 * Taking a post off the calendar (2026-09-29, the owner: *"we need a delete post in the future"*): one waiting for its
 * time, or one that stopped and is not worth another go. Asked once on the card itself, not in a browser box.
 *
 * A post that went out can be removed too (2026-09-30, the owner, who had deleted one on Instagram during a demo and
 * found it still here). That does not reach the platform, so `note` says so while it asks: nobody should believe a
 * press here took a post down anywhere.
 */
function TakeBack({ id, label, yes, note }: { id: string; label: string; yes: string; note?: string }) {
  const qc = useQueryClient();
  const [asking, setAsking] = useState(false);
  const take = useMutation({
    mutationFn: () => api.cancelPost(id),
    onSuccess: () => {
      for (const key of [["calendar"], ["made"], ACTIVITY_KEY]) void qc.invalidateQueries({ queryKey: key });
    },
  });
  const small = "px-2 py-1 rounded-md text-[10.5px] font-semibold disabled:opacity-50";
  return (
    <div className="flex flex-col gap-1 items-start">
      {asking && note ? <p className="text-[10.5px] leading-snug text-ink-2">{note}</p> : null}
      {asking ? (
        <div className="flex items-center gap-1.5">
          <button onClick={() => take.mutate()} disabled={take.isPending} className={`${small} bg-bad text-white`}>{yes}</button>
          <button onClick={() => setAsking(false)} disabled={take.isPending} className={`${small} border border-line-2 bg-panel hover:bg-page`}>Keep it</button>
        </div>
      ) : (
        <button onClick={() => setAsking(true)} className={`${small} border border-line-2 bg-panel text-muted hover:text-ink hover:bg-page`}>{label}</button>
      )}
      {take.error ? <p role="alert" className="text-[10.5px] text-bad">{String(take.error)}</p> : null}
    </div>
  );
}

/** Another go at a failed post, after its owner has done what the card says. What came of it is the card's to show. */
function Retry({ id, onSent }: { id: string; onSent: (id: string) => void }) {
  const qc = useQueryClient();
  const retry = useMutation({
    mutationFn: () => api.retryPost(id),
    onSuccess: () => { onSent(id); void qc.invalidateQueries({ queryKey: ["calendar"] }); },
  });
  return (
    <div className="flex flex-col gap-1 items-start">
      <button
        onClick={() => retry.mutate()}
        disabled={retry.isPending}
        className="px-2 py-1 rounded-md border border-line-2 bg-panel text-[10.5px] font-semibold hover:bg-page disabled:opacity-50"
      >
        Retry
      </button>
      {retry.error ? <p role="alert" className="text-[10.5px] text-bad">{String(retry.error)}</p> : null}
    </div>
  );
}

/** The days a view covers. `startToday` runs the grid from today rather than from the first of the month (08). */
function grid(view: View, startToday: boolean): Date[] {
  const now = new Date();
  const from = new Date(now);
  if (view === "list" || view === "queue") return Array.from({ length: 30 }, (_, i) => add(from, i));
  if (view === "week") {
    if (!startToday) from.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    return Array.from({ length: 7 }, (_, i) => add(from, i));
  }
  if (startToday) return Array.from({ length: 42 }, (_, i) => add(from, i));
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  first.setDate(1 - ((first.getDay() + 6) % 7));
  return Array.from({ length: 42 }, (_, i) => add(first, i));
}

const add = (d: Date, days: number) => { const x = new Date(d); x.setDate(d.getDate() + days); return x; };
const key = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const month = () => new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" });
